// @flow

import React, { type Node, useState } from 'react';
import { get } from 'lodash';

import { Query } from '@apollo/react-components';
import { ApolloConsumer, type ApolloError } from '@apollo/react-common';

import * as teamMemberQueries from './zapTransferTeamMemberQueries';

import type {
  OnSearchMembersType,
  TeamMemberType,
} from '@zapier/team/src/types';
import type { Page } from '@zapier/common-components';

type ChildrenProps = {
  count: number,
  error: ?ApolloError,
  isLoading: boolean,
  isWorking: boolean,
  onLoadMoreTeamMembers: Page => void,
  onSearchMembers: OnSearchMembersType,
  teamMembers: Array<TeamMemberType>,
};

type Props = {
  accountId: ?number,
  children: ChildrenProps => Node,
};

const WithTeamMembers = ({ accountId, children }: Props) => {
  // fetchMore doesn't return errors so we have to catch them and set them
  const [fetchMoreError, setFetchMoreError] = useState(null);

  const variables = { accountId };

  return (
    <Query
      notifyOnNetworkStatusChange={true}
      query={teamMemberQueries.teamMemberQuery}
      skip={!accountId}
      // $FlowFixMe doesn't like variables missing id limit and offset
      variables={variables}
    >
      {queryResults => (
        <ApolloConsumer>
          {client => {
            const handleLoadMoreTeamMembers = ({ pageSize, start }) => {
              // Get More members!
              queryResults
                .fetchMore({
                  updateQuery: (prev, { fetchMoreResult }) => {
                    // We didn't get anything so nothing to update
                    if (!fetchMoreResult) return prev;

                    // Update the cache
                    return {
                      ...fetchMoreResult,
                      account: {
                        ...prev.account,
                        team: {
                          ...fetchMoreResult.account.team,
                          members: {
                            ...fetchMoreResult.account.team.members,
                            edges: [
                              ...prev.account.team.members.edges,
                              ...fetchMoreResult.account.team.members.edges,
                            ],
                          },
                        },
                      },
                    };
                  },
                  // $FlowFixMe doesn't like variables missing id limit and offset
                  variables: {
                    id: accountId,
                    limit: pageSize,
                    offset: start,
                  },
                })
                .catch(e => {
                  setFetchMoreError(e.message);
                });
            };

            const handleMemberSearch = async search => {
              const results = await client.query({
                query: teamMemberQueries.searchMembersQuery,
                variables: {
                  accountId,
                  search,
                },
              });
              return get(results, ['data', 'searchMembers', 'edges'], []);
            };

            const isWorking = queryResults.loading;

            return children({
              count: get(
                queryResults,
                ['data', 'account', 'team', 'members', 'totalCount'],
                0
              ),
              error: queryResults.error || fetchMoreError,
              isLoading: queryResults.loading,
              isWorking,
              onLoadMoreTeamMembers: handleLoadMoreTeamMembers,
              onSearchMembers: handleMemberSearch,
              teamMembers: get(
                queryResults,
                ['data', 'account', 'team', 'members', 'edges'],
                []
              ),
            });
          }}
        </ApolloConsumer>
      )}
    </Query>
  );
};

export default WithTeamMembers;
